import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';

interface FormFieldProps {
  label: string;
  name: string;
  error?: string;
  hint?: string;
  required?: boolean;
  /** 364: say "(optional)" beside the label, for a field the reader might expect to be required. */
  optional?: boolean;
  children: ReactNode;
}

export function FormField({
  label,
  name,
  error,
  hint,
  required,
  optional,
  children,
}: FormFieldProps) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={name} className="text-sm font-medium text-ink">
        {label}
        {required && <span className="ml-1 text-red-500">*</span>}
        {optional && <span className="ml-1 font-normal text-steel">(optional)</span>}
      </label>
      {children}
      {hint && !error && <p className="text-xs text-steel">{hint}</p>}
      {/*
        061: announced. A field error appears after a submit attempt, so a screen-reader user is
        otherwise never told why nothing happened — and the 061 rate-card owner refusal is stated here
        INSTEAD of a platform response, because it is raised before any request is sent (FR-011).
        Additive: the text and styling are unchanged for all existing consumers.
      */}
      {error && (
        <p role="alert" className="text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

const inputBase =
  'block w-full rounded-lg border border-hairline bg-canvas text-ink px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:bg-surface disabled:text-muted';

export function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={[inputBase, props.className].join(' ')} />;
}

export function Textarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      {...props}
      rows={props.rows ?? 3}
      className={[inputBase, 'resize-y', props.className].join(' ')}
    />
  );
}

export function Select({ children, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select {...props} className={[inputBase, 'cursor-pointer', props.className].join(' ')}>
      {children}
    </select>
  );
}
